import {
    Button,
    Checkbox,
    Group,
    Input,
    Popover,
    Stack,
    Text,
    TextInput,
    Tooltip
} from "@mantine/core";
import {WarningCircleIcon} from "@phosphor-icons/react";
import {useEffect, useMemo, useRef, useState} from "react";
import Icon from "../../../../components/Icon/Icon.tsx";
import IconMap from "../../../../components/Icon/IconMap.ts";
import classes from "../ProductionModulePlannerPage.module.css";
import {LocationSelectorGroup, LocationSelectorOption} from "../plannerTypes.ts";

type ProductionPlannerLocationGroupSelectorProps = {
    label: string;
    placeholder: string;
    groups: LocationSelectorGroup[];
    optionById: Map<string, LocationSelectorOption>;
    value: string[];
    className?: string;
    nothingFoundMessage?: string;
    onChange: (value: string[]) => void;
};

export default function ProductionPlannerLocationGroupSelector({
    label,
    placeholder,
    groups,
    optionById,
    value,
    className,
    nothingFoundMessage = "No locations found",
    onChange,
}: ProductionPlannerLocationGroupSelectorProps) {
    const [opened, setOpened] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!opened) {
            setSearchValue("");
            return;
        }
        const timerId = window.setTimeout(() => {
            searchInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [opened]);

    const selectedSet = useMemo(() => new Set(value), [value]);
    const normalizedSearch = searchValue.trim().toLowerCase();

    const filteredGroups = useMemo(() => {
        if (normalizedSearch === "") {
            return groups;
        }
        return groups
            .map((group) => {
                const groupMatches = group.name.toLowerCase().includes(normalizedSearch);
                const entityIds = groupMatches
                    ? group.entityIds
                    : group.entityIds.filter((entityId) => {
                        const option = optionById.get(entityId);
                        if (!option) {
                            return false;
                        }
                        return option.name.toLowerCase().includes(normalizedSearch)
                            || option.typeName.toLowerCase().includes(normalizedSearch);
                    });
                return {
                    ...group,
                    entityIds,
                };
            })
            .filter((group) => group.entityIds.length > 0);
    }, [groups, normalizedSearch, optionById]);

    const toggleLocation = (entityId: string) => {
        const option = optionById.get(entityId);
        if (!option || option.disabled) {
            return;
        }
        if (selectedSet.has(entityId)) {
            onChange(value.filter((current) => current !== entityId));
            return;
        }
        onChange([...value, entityId]);
    };

    const toggleGroup = (group: LocationSelectorGroup, checked: boolean) => {
        const selectableIds = group.entityIds.filter((entityId) => {
            const option = optionById.get(entityId);
            return !!option && !option.disabled;
        });
        if (selectableIds.length === 0) {
            return;
        }
        if (checked) {
            const selectedInGroup = new Set(selectableIds);
            onChange(value.filter((entityId) => !selectedInGroup.has(entityId)));
            return;
        }
        const next = [...value];
        selectableIds.forEach((entityId) => {
            if (!selectedSet.has(entityId)) {
                next.push(entityId);
            }
        });
        onChange(next);
    };

    return (
        <Input.Wrapper label={label} className={className}>
            <Popover
                opened={opened}
                onChange={setOpened}
                position="bottom-start"
                width="target"
                withArrow
                trapFocus={false}
                shadow="md"
            >
                <Popover.Target>
                    <Button
                        variant="default"
                        className={classes.locationSelectorTrigger}
                        onClick={() => setOpened((current) => !current)}
                    >
                        {value.length > 0 ? `${value.length} selected` : placeholder}
                    </Button>
                </Popover.Target>
                <Popover.Dropdown className={classes.locationSelectorDropdown}>
                    <Stack gap="xs">
                        <TextInput
                            ref={searchInputRef}
                            size="xs"
                            placeholder="Search groups or locations"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.currentTarget.value)}
                        />
                        <div className={classes.locationSelectorList}>
                            {filteredGroups.length === 0 ? (
                                <Text size="sm" c="dimmed">{nothingFoundMessage}</Text>
                            ) : filteredGroups.map((group) => {
                                const groupOptions = group.entityIds
                                    .map((entityId) => optionById.get(entityId))
                                    .filter((option): option is LocationSelectorOption => !!option);
                                const selectableCount = groupOptions.filter((option) => !option.disabled).length;
                                const selectedCount = groupOptions.filter((option) => !option.disabled && selectedSet.has(option.id)).length;
                                const checked = selectableCount > 0 && selectedCount === selectableCount;
                                const indeterminate = selectedCount > 0 && selectedCount < selectableCount;

                                return (
                                    <div key={group.id} className={classes.locationGroupSection}>
                                        <Checkbox
                                            checked={checked}
                                            indeterminate={indeterminate}
                                            disabled={selectableCount === 0}
                                            label={`${group.name} (${selectedCount}/${selectableCount})`}
                                            onChange={() => toggleGroup(group, checked)}
                                        />
                                        <Stack gap={4} className={classes.locationGroupItems}>
                                            {groupOptions.map((option) => (
                                                <Checkbox
                                                    key={option.id}
                                                    checked={selectedSet.has(option.id)}
                                                    disabled={option.disabled}
                                                    onChange={() => toggleLocation(option.id)}
                                                    label={(
                                                        <Group gap={6} wrap="nowrap" className={classes.locationOptionLabel}>
                                                            {option.warning === "active" ? (
                                                                <Tooltip label="Active action">
                                                                    <WarningCircleIcon
                                                                        size={14}
                                                                        weight="fill"
                                                                        className={classes.warningIconActive}
                                                                    />
                                                                </Tooltip>
                                                            ) : option.warning === "queued" ? (
                                                                <Tooltip label="Queued actions">
                                                                    <WarningCircleIcon
                                                                        size={14}
                                                                        weight="fill"
                                                                        className={classes.warningIconQueued}
                                                                    />
                                                                </Tooltip>
                                                            ) : null}
                                                            {option.hasDatacard ? (
                                                                <Tooltip label="Datacard assigned">
                                                                    <span className={classes.datacardIcon}>
                                                                        <Icon icon={IconMap.Datacard} size={14}/>
                                                                    </span>
                                                                </Tooltip>
                                                            ) : null}
                                                            <Text size="sm">{option.name}</Text>
                                                            <Text size="xs" c="dimmed">({option.typeName})</Text>
                                                        </Group>
                                                    )}
                                                />
                                            ))}
                                        </Stack>
                                    </div>
                                );
                            })}
                        </div>
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        </Input.Wrapper>
    );
}
